/* eslint-disable @next/next/no-img-element */
import { ReactNode, useRef, useState } from "react"
import { ColorBlur } from "../ColorBlur"
import { HoverSkew } from "../HoverSkew"
import { AllReviews, Heading, LargeHeading, Paragraph, Review, Section } from "../misc"
import Hero from "public/marketing/triggers.png"
import { useScrollAnimation } from "./Customers"

const StarIcon = (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
    <title>shape-star</title>
    <g fill="currentColor">
      <path
        d="M23.144,8.541,16.063,7.512,12.9,1.1a1.041,1.041,0,0,0-1.794,0L7.937,7.512.856,8.541A1,1,0,0,0,.3,10.246L5.425,15.24,4.216,22.293a1,1,0,0,0,1.451,1.054L12,20.018l6.333,3.329a1,1,0,0,0,1.451-1.054L18.575,15.24,23.7,10.246a1,1,0,0,0-.554-1.705Z"
        fill="currentColor"
      ></path>
    </g>
  </svg>
)

export const Reviews = ({
  title = `Don't take our word for it`,
  children,
}: {
  title?: string
  children?: ReactNode
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const { isVisible, percentage } = useScrollAnimation(ref)

  return (
    <Section className="text-left">
      <ColorBlur></ColorBlur>
      <div className="flex flex-col gap-1 text-center items-center pb-10" ref={ref}>
        <div className="scale-75 md:scale-90 !max-w-[750px] flex flex-row items-center justify-center gap-3 !text-opacity-100 bg-[#F8C574] bg-opacity-5 p-6 py-3 rounded-full text-[#F8C574] mb-5 border border-2 border-[#F8C574] border-opacity-10">
          {StarIcon}
          {StarIcon}
          {StarIcon}
          {StarIcon}
          {StarIcon}
        </div>
        <LargeHeading>{title}</LargeHeading>
        <Paragraph className="!max-w-[750px] !text-opacity-60">{`Our customers refer to Superwall as their most impactful monetization tool. In their own words:`}</Paragraph>
      </div>
      {children}
      <Testimonials showing={percentage < 0.6} />
    </Section>
  )
}

const Testimonials = ({ showing }: { showing: boolean }) => {
  const featured: Review = AllReviews[0]!

  // slice the reviews by chunking them into groups of 4
  let left = Array<Review>()
  let leftMiddle = Array<Review>()
  let rightMiddle = Array<Review>()
  let right = Array<Review>()

  AllReviews.filter((review) => review.text).forEach((review, index) => {
    if (index > 0) {
      if (index % 4 === 1) {
        left.push(review)
      }
      if (index % 4 === 2) {
        right.push(review)
      }
      if (index % 4 === 3) {
        leftMiddle.push(review)
      }
      if (index % 4 === 0) {
        rightMiddle.push(review)
      }
    }
  })

  const testimonials: Array<Array<Array<Review>>> = [
    [left, leftMiddle],
    [rightMiddle, right],
  ]

  return (
    <div className="relative isolate">
      <div className="mx-auto max-w-8xl">
        <div className="mx-auto grid max-w-2xl grid-cols-1 grid-rows-1 gap-3 md:gap-4 text-sm sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-flow-col lg:grid-cols-4">
          <div
            className={`col-span-1 sm:col-span-2 lg:col-start-2 lg:row-end-1 transition-all delay-100 ease-in-out duration-[0.6s] ${
              showing ? "opacity-100 scale-100" : "opacity-0 translate-y-20"
            }`}
          >
            <HoverSkew className={`!px-7 !py-5 !rounded-[10px]`}>
              <figure className="">
                <FigTop testimonial={featured} />
                <blockquote className="text-xl md:text-2xl tracking-tight opacity-90">
                  {featured.text}
                </blockquote>
                <FigCaption testimonial={featured} />
              </figure>
            </HoverSkew>
          </div>
          {testimonials.map((columnGroup, columnGroupIdx) => (
            <div key={columnGroupIdx} className="space-y-3 md:space-y-4 lg:contents lg:space-y-0">
              {columnGroup.map((column, columnIdx) => (
                <div
                  key={columnIdx}
                  className={classNames(
                    (columnGroupIdx === 0 && columnIdx === 0) ||
                      (columnGroupIdx === testimonials.length - 1 &&
                        columnIdx === columnGroup.length - 1)
                      ? "lg:row-span-2"
                      : "lg:row-start-1",
                    "space-y-3 md:space-y-4"
                  )}
                >
                  {column.map((testimonial, rowIdx) => (
                    <div
                      key={testimonial.author}
                      style={{
                        transitionDelay: `${300 + rowIdx * 300}ms`,
                      }}
                      className={`transition-all duration-[0.6s] ease-in-out ${
                        showing
                          ? "translate-y-0 opacity-100 scale-100"
                          : "translate-y-0 opacity-0 translate-y-20"
                      }`}
                    >
                      <HoverSkew className={`!px-7 !py-5 !rounded-[10px]`}>
                        <figure className="">
                          <FigTop testimonial={testimonial} />
                          <blockquote className="text-white text-xl tracking-tight opacity-90">
                            {testimonial.text}
                          </blockquote>
                          <FigCaption testimonial={testimonial} />
                        </figure>
                      </HoverSkew>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const FigTop = ({ testimonial }) => {
  return (
    <>
      {/* <Paragraph className="!max-w-[750px] flex flex-row items-center justify-start gap-2 !text-base !text-opacity-100 text-[#F8C574] mb-5">
        {StarIcon}
        {StarIcon}
        {StarIcon}
        {StarIcon}
        {StarIcon}
      </Paragraph> */}
    </>
  )
}

const FigCaption = ({ testimonial }) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <figcaption
      className="mt-2 cursor-pointer"
      onMouseMove={() => {
        if (!isHovered) setIsHovered(true)
      }}
      onMouseEnter={() => {
        if (!isHovered) setIsHovered(true)
      }}
      onMouseLeave={() => {
        if (isHovered) setIsHovered(false)
      }}
    >
      <a href={testimonial.website} target="_blank" rel="noreferrer">
        <div className=" flex text-left items-center gap-x-4 mt-4 border-t border-white pt-4 border-opacity-10 -ml-7 -mr-7 px-5 -mb-1">
          <img className="h-12 w-12 rounded-[30%] shadow-xl" src={testimonial.imageUrl} alt="" />
          <div className="flex flex-col items-start gap-0">
            <div className="text-white text-lg tracking-tigh flex items-center gap-2">
              {testimonial.company}{" "}
              <span
                className={`transition-all duration-400 ${
                  isHovered ? "opacity-40 scale-100" : "opacity-0 scale-[0.90] origin-left"
                }`}
              >
                {OpenSVG}
              </span>
            </div>
            <div className="text-white text-base opacity-50 tracking-tight -mt-1">
              {`${testimonial.author}, ${testimonial.jobTitle}`}
            </div>
          </div>
        </div>
      </a>
    </figcaption>
  )
}

const OpenSVG = (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 16 16">
    <title>launch</title>
    <g fill="#ffffff">
      <path
        data-color="color-2"
        d="M15.5,0h-5a.5.5,0,0,0-.354.854l1.793,1.792L6.293,8.293A1,1,0,1,0,7.707,9.707l5.647-5.646,1.792,1.793A.5.5,0,0,0,15.5,6a.512.512,0,0,0,.191-.038A.5.5,0,0,0,16,5.5V.5A.5.5,0,0,0,15.5,0Z"
      ></path>
      <path
        d="M13,9a1,1,0,0,0-1,1v4H2V4H6A1,1,0,0,0,6,2H2A2,2,0,0,0,0,4V14a2,2,0,0,0,2,2H12a2,2,0,0,0,2-2V10A1,1,0,0,0,13,9Z"
        fill="#ffffff"
      ></path>
    </g>
  </svg>
)

function classNames(...classes) {
  return classes.filter(Boolean).join(" ")
}
